import React from 'react';
import { ArrowLeft } from 'lucide-react';

interface TermsAndConditionsProps {
  onBack: () => void;
}

export default function TermsAndConditions({ onBack }: TermsAndConditionsProps) {
  return (
    <div className="min-h-screen bg-white text-zinc-900 selection:bg-purple-100 selection:text-purple-900 relative overflow-hidden font-sans">
      {/* Colorful Gradient Backdrop */}
      <div className="absolute inset-x-0 top-0 h-[600px] pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-15%] w-[55%] h-[60%] rounded-full bg-purple-200/35 blur-[120px]" />
        <div className="absolute top-[-5%] right-[-10%] w-[50%] h-[55%] rounded-full bg-orange-100/40 blur-[110px]" />
        <div className="absolute top-[20%] left-[25%] w-[45%] h-[50%] rounded-full bg-blue-100/30 blur-[130px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-20">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="group flex items-center gap-2 text-sm font-semibold text-zinc-550 hover:text-zinc-950 transition-colors mb-12 cursor-pointer"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        {/* Article Container */}
        <article className="space-y-10">
          <header className="space-y-4 border-b border-zinc-100 pb-8">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-purple-700 uppercase">
              BETA Agreement
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-950">
              Terms & Conditions
            </h1>
            <p className="text-sm font-semibold text-zinc-400">
              Last Updated: June 26, 2026
            </p>
          </header>

          <section className="space-y-6 text-[15px] leading-relaxed text-zinc-650 font-medium">
            <p>
              By accessing or using the <strong>Design Studio</strong> platform, you agree to comply with and be bound by the following Terms & Conditions. Please read these terms carefully before accessing the application.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">1. Brand Identity and Intellectual Property</h2>
            <p>
              Design Studio and its associated service markings, source code repositories, visual representations, layout patterns, and templates are properties developed and maintained by <strong>Caselgrid</strong>. Accessing the workspace tools does not grant any ownership rights of any source materials or features.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">2. Beta Operational Limitations</h2>
            <p>
              Design Studio is currently distributed as an <strong>experimental beta test application</strong>. Active updates, modifications, and experimental changes occur daily. You acknowledge and accept that:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>The service may contain security bugs, rendering errors, save failures, and functional anomalies.</li>
              <li>Workspace items, whiteboards, notes, and uploaded layouts can be deleted, reset, or become permanently inaccessible without prior notice.</li>
              <li>Production dependencies should not rely on the availability of this platform.</li>
            </ul>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">3. Absolute Limitation of Liability & User Risk</h2>
            <p className="bg-red-50/50 border border-red-100 rounded-2xl p-5 text-red-950 text-sm">
              <strong>CRITICAL DISCLAIMER:</strong> Your engagement with the site, features, editor, and sandbox elements is <strong>wholly at your own individual risk</strong>. Caselgrid, its directors, developers, and licensing partners accept absolutely no responsibility or legal liability for damages, direct loss, copyright infringement issues, or business interruptions that might occur. No guarantees of merchantability or fitness for a particular purpose are made.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">4. Acceptable Conduct</h2>
            <p>
              You agree not to deploy automated crawlers, excessive scraping bots, or perform malicious code injections that disrupt the whiteboard syncing operations. Violation of operational rules will lead to immediate lock of access to the workspace.
            </p>

            <h2 className="text-xl font-bold text-zinc-900 pt-4">5. Governing Law</h2>
            <p>
              Any disputes arising out of the use of Design Studio shall be governed by and construed in accordance with the laws of the jurisdiction of Caselgrid's registration.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
